import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import Sidebar from "./Sidebar";
import MobileTabBar from "./MobileTabBar";
import { useSidebarStore } from "../stores/sidebarStore";
import {
  BELOW_HEADER_MIN_H,
  SIDEBAR_HIDDEN_PATHS,
} from "../constants/layout";

type MainLayoutProps = {
  children: ReactNode;
};

export default function MainLayout({ children }: MainLayoutProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  
  // 🚀 Read the manual toggle state
  const isCollapsed = useSidebarStore((state) => state.isCollapsed);
  // The CV builder hides the sidebar once you're editing — Save & Exit is the
  // way back out, so the nav rail is dead weight over the editing surface.
  const isSidebarHidden = useSidebarStore((state) => state.isSidebarHidden);

  // Check if current page is in the explicit no-sidebar list
  const isSidebarHiddenPage =
    SIDEBAR_HIDDEN_PATHS.includes(location.pathname) ||
    location.pathname.startsWith("/blog/") ||
    location.pathname.startsWith("/p/");

  // Check if session hint exists (user previously logged in)
  const hasSessionHint = (() => {
    try {
      return (
        localStorage.getItem("rx_session_hint") === "1" ||
        Boolean(sessionStorage.getItem("local_auth_token")) ||
        document.cookie.includes("accessToken=")
      );
    } catch {
      return false;
    }
  })();

  const isLikelyAuthenticated = Boolean(user || (isLoading && hasSessionHint));

  // Only show sidebar if:
  // 1. User is authenticated (or likely authenticated with session hint)
  // 2. Current page is not in the explicit no-sidebar list
  // 3. Not manually hidden
  const showSidebar =
    isLikelyAuthenticated && !isSidebarHiddenPage && !isSidebarHidden;
  const isCvBuilderRoute =
    location.pathname.startsWith("/cv-builder") ||
    location.pathname.startsWith("/editor");
  const isAiAssistantRoute = location.pathname.startsWith("/ai-assistant");
  const isAiCvBuilderRoute = location.pathname.startsWith("/ai-cv-builder");
  const isImmersiveRoute =
    isCvBuilderRoute || isAiAssistantRoute || isAiCvBuilderRoute;
  const showMobileTabBar = Boolean(showSidebar) && !isCvBuilderRoute;

  // Only the cv-builder route forces a fixed collapsed-width margin (sidebar auto-collapses
  // there and hover-overlays the content). Every other page — including AIAssistant — must
  // follow the user's manual toggle so the content shifts right when the sidebar expands.
  const desktopMarginClass = showSidebar
    ? isCollapsed
      ? "md:ml-20"
      : "md:ml-72"
    : "";

  return (
    <div
      className={`overflow-x-hidden bg-gray-50 ${
        isImmersiveRoute ? "h-screen overflow-hidden" : BELOW_HEADER_MIN_H
      }`}
    >
      {showSidebar && <Sidebar />}
      <div
        className={`flex flex-col transition-[margin] duration-300 ${desktopMarginClass} ${
          isImmersiveRoute ? "h-full overflow-hidden" : BELOW_HEADER_MIN_H
        } ${showMobileTabBar ? "pb-24 md:pb-0" : ""}`}
      >
        <main
          className={`${
            isImmersiveRoute ? "flex-1 overflow-hidden" : "flex-1"
          }`}
        >
          {children}
        </main>
      </div>
      {showMobileTabBar && <MobileTabBar />}
    </div>
  );
}